import { useLang } from '../context/LanguageContext';
import LegalPageShell from '../components/LegalPageShell';
import { usePageMeta } from '../hooks/usePageMeta';
import { LEGAL_TE_SUMMARIES } from '../content/legalContent';
import { BUSINESS, businessAddressBlock, businessPhoneDisplay, businessTelHref } from '../config/business';

export function CancellationContent() {
  return (
    <>
      <p className="legal-updated">Last updated: June 2026</p>

      <p className="legal-highlight">
        Repatikosam is a one-time annual payment — no auto-debit. To cancel access, email{' '}
        <a href={`mailto:${BUSINESS.email}`}>{BUSINESS.email}</a> with your registered Google email.
      </p>

      <div className="legal-section">
        <h3>1. Overview</h3>
        <p>
          Repatikosam sells an <strong>annual digital subscription</strong> (12 months of e-paper and member
          access) as a <strong>one-time payment</strong> through Razorpay. We do not charge recurring monthly
          auto-debit by default. This Cancellation Policy explains how you can stop using the service and what
          happens to your access.
        </p>
      </div>

      <div className="legal-section">
        <h3>2. Before You Subscribe</h3>
        <p>
          You may use free preview features where available before paying. The total price in INR (₹999 + applicable
          GST) is shown on the Razorpay checkout screen before you complete payment. International customers see the
          same INR amount; any currency conversion is handled by your bank or card issuer.
        </p>
      </div>

      <div className="legal-section">
        <h3>3. How to Cancel / Stop Access</h3>
        <p>To cancel or close your subscription access, email us with your registered Google email:</p>
        <ul>
          <li>
            Email: <strong>{BUSINESS.email}</strong>
            <br />
            Phone / WhatsApp: <a href={businessTelHref()}>{businessPhoneDisplay()}</a>
          </li>
          <li>Subject: <strong>Cancellation Request</strong></li>
          <li>Include your payment receipt or Razorpay payment ID if available</li>
        </ul>
        <p>
          We will confirm cancellation within <strong>2 business days</strong>. Because our plan is a one-time
          annual purchase, cancellation means we will not renew or charge you again until you choose to subscribe
          anew.
        </p>
      </div>

      <div className="legal-section">
        <h3>4. Access After Cancellation</h3>
        <p>
          If you cancel during an active paid period, you will generally retain access until the end of your
          already-paid subscription term, unless a refund is approved under our{' '}
          <a href="/refund">Refund Policy</a>. We do not provide partial refunds for unused months except as
          stated in the refund policy.
        </p>
      </div>

      <div className="legal-section">
        <h3>5. No Further Charges</h3>
        <p>
          After cancellation, no further payments will be taken for the same subscription period. We do not store your
          card details — Razorpay processes each payment separately. If you subscribe again in the future, a new
          one-time payment will be required.
        </p>
      </div>

      <div className="legal-section">
        <h3>6. Refunds</h3>
        <p>
          Refund eligibility is governed separately by our <a href="/refund">Refund Policy</a>. Cancellation and
          refund requests may be submitted together; we will respond according to both policies.
        </p>
      </div>

      <div className="legal-section">
        <h3>7. Contact</h3>
        <p>
          📧 <strong>{BUSINESS.email}</strong>
          <br />
          📍 {businessAddressBlock()}
          <br />
          GSTIN: {BUSINESS.gstin}
        </p>
      </div>
    </>
  );
}

export default function CancellationPage({ onClose, standalone = false }) {
  const { t } = useLang();
  usePageMeta({
    title: `${t.footerCancellation} | ${t.brandName}`,
    description: `Cancellation policy for Repatikosam annual subscription.`,
    path: '/cancellation',
    enabled: standalone,
  });
  return (
    <LegalPageShell
      title={t.footerCancellation}
      onClose={onClose}
      standalone={standalone}
      teSummary={LEGAL_TE_SUMMARIES.cancellation}
    >
      <CancellationContent />
    </LegalPageShell>
  );
}
